import { ActionIcon, Box, Divider, Group, Paper, ScrollArea, Text, Tooltip } from '@mantine/core';
import { IconBook, IconBookOff, IconDeviceFloppy, IconDownload, IconPlus } from '@tabler/icons-react';
import { useDispatch } from 'react-redux';
import { Show } from '../../../../../../react-commons/components/control-flow/Show';
import { HeightFittedStack } from '../../../../../../react-commons/components/data-display/HeightFittedStack';
import { Form_RichText } from '../../../../../../react-commons/components/form/inputs/Form_RichText';
import { Form_StringInput } from '../../../../../../react-commons/components/form/inputs/Form_StringInput';
import { Controlled_RichText } from '../../../../../../react-commons/components/inputs/controlled/Controlled_RichText';
import { useMyForm } from '../../../../../../react-commons/hooks/useMyForm';
import { OpenModal } from '../../../../../../react-commons/utils/modals/openModal';
import { ControlledInputs } from '../../../../../../react-commons/utils/read-only-inputs/controlledInputs';
import { setImportingOrExportingState, setTaskRefresh } from '../../../../persisted/global/globalActions';
import { ResourceType } from '../../../../resource/enumerations/ResourceType';
import { VersionState } from '../../../../resource/enumerations/VersionState';
import {
    defaultMethodResource,
    MethodMutationResourceSchema,
} from '../../../../resource/resources/artifacts/method/methodResource';
import {
    ProjectPropertyValidation,
    useProjectActions,
} from '../../../../resource/resources/artifacts/project/projectResource';
import {
    defaultProjectVersionPublishmentResource,
    ProjectVersionPublishmentMutationResourceSchema,
    useProjectVersionPublishmentActions,
} from '../../../../resource/resources/artifacts/project/projectVersionPublishmentResource';
import {
    defaultProjectVersionResource,
    ProjectVersionMutationResourceSchema,
    ProjectVersionPropertyValidation,
    useProjectVersionActions,
} from '../../../../resource/resources/artifacts/project/projectVersionResource';
import { useUserActions } from '../../../../resource/resources/persistables/user/userResource';
import { startDownloadPVT } from '../../../../resource/services/system/systemActions';
import { useThunkDispatch } from '../../../../store/rootReducer';
import { useUserId } from '../../../authentication/useUserId';
import { PropertyInputs } from '../../../reusable-components/base/property-bases/PropertyInputs';
import { ControlledPropertyInputs } from '../../../reusable-components/base/property-bases/read-only/ControlledPropertyInputs';
import { DisableableIconButtonWithTooltip } from '../../../reusable-components/DisableableIconButtonWithTooltip';
import { useDeleteResourceDialogFunction } from '../../../reusable-components/dialogs/UseDeleteResourceDialogFunction';
import { Form_EditableViewWrapper } from '../../../reusable-components/form-components/Form_EditableViewWrapper';
import { ResourceValidationTitleBar } from '../../../reusable-components/validation/ResourceValidationTitleBar';
import { VersionInfoBarDisplay } from '../../../reusable-components/version-displays/VersionInfoBarDisplay';

export const ProjectVersionDetailsComponent = ({
    projectId,
    projectVersionId,
}: {
    projectId: string;
    projectVersionId: string;
}) => {
    const userId = useUserId('');
    const dispatch = useDispatch();
    const thunkDispatch = useThunkDispatch();
    const deleteResourceDialogFunction = useDeleteResourceDialogFunction();
    const projectVersionActions = useProjectVersionActions();
    const projectActions = useProjectActions();
    const projectVersionPublishmentActions = useProjectVersionPublishmentActions();
    const userActions = useUserActions();
    const userSystemRights = userActions.useGetUserEffectiveRightsOfUser(userId, projectId);
    const project = projectActions.useGetResource(projectId);
    const projectVersion = projectVersionActions.useGetResource(projectVersionId);
    const updateProjectVersion = projectVersionActions.useUpdateResource();
    const projectVersionPublishment = projectVersionPublishmentActions.useGetSingleResource(projectVersionId);

    const closeProjectVersion = projectVersionActions.useCloseVersion();
    const openProjectVersion = projectVersionActions.useUnlockVersion();
    const lockProjectVersion = projectVersionActions.useLockVersion();

    const addPublishment = projectVersionPublishmentActions.useSetResource();
    const deletePublishment = projectVersionPublishmentActions.useDeleteResource();
    const updatePublishment = projectVersionPublishmentActions.useUpdateResource();

    const { form, formNames, onSubmit } = useMyForm({
        schema: ProjectVersionMutationResourceSchema,
        defaultValues: defaultProjectVersionResource,
        submitHandler: async (data) => {
            await updateProjectVersion.mutateAsync({
                resource: data,
                id: data.id,
            });
        },
        reactiveValues: projectVersion.data,
    });

    const {
        form: publishmentForm,
        formNames: publishmentFormNames,
        onSubmit: publishmentOnSubmit,
    } = useMyForm({
        schema: ProjectVersionPublishmentMutationResourceSchema,
        defaultValues: defaultProjectVersionPublishmentResource,
        submitHandler: async (data) => {
            await updatePublishment.mutateAsync({
                resource: data,
                id: data.id,
            });
        },
        reactiveValues: projectVersionPublishment.data,
    });

    const isProcessAdmin = userSystemRights.data?.isProcessAdmin;
    const isAdmin = userSystemRights.data?.isAdmin;
    return (
        <HeightFittedStack>
            <Show when={isProcessAdmin}>
                <ResourceValidationTitleBar metaDataInfo={project.data} resourceType={ResourceType.PROJECT}>
                    <Text c={'var(--mantine-text-secondary-color)'}>Project</Text>
                </ResourceValidationTitleBar>
                <ControlledPropertyInputs.Name
                    value={project.data?.name ?? ''}
                    readOnly={true}
                    propertyValidationInformation={{
                        resourceType: ResourceType.PROJECT,
                        resourceId: project.data?.id ?? '',
                        propertyIdentifier: ProjectPropertyValidation.NAME,
                    }}
                />
                <Divider size={'sm'} />
                <HeightFittedStack>
                    <Form_EditableViewWrapper form={publishmentForm} onSubmit={publishmentOnSubmit}>
                        <ResourceValidationTitleBar
                            metaDataInfo={projectVersion.data}
                            resourceType={ResourceType.PROJECT_VERSION}
                        >
                            <Text c={'var(--mantine-text-secondary-color)'}>Project Version</Text>
                        </ResourceValidationTitleBar>
                        <VersionInfoBarDisplay
                            isPublished={Boolean(projectVersionPublishment.data)}
                            versionInfo={projectVersion.data?.versionInfo ?? null}
                            versionId={projectVersion.data?.id ?? ''}
                            versionStateLabel={'Version State'}
                            versionOnly={false}
                            versionResourceType={ResourceType.PROJECT_VERSION}
                            versionInfoPropertyIdentifier={ProjectVersionPropertyValidation.VERSION_INFO}
                            customButtonIsIcon
                            CustomButtons={
                                isProcessAdmin ?
                                    <Group>
                                        {projectVersionPublishment.data ?
                                            <Tooltip
                                                label={`Delete Publishment : ${projectVersionPublishment.data?.title}`}
                                            >
                                                <ActionIcon
                                                    size={'sm'}
                                                    variant={'transparent'}
                                                    onClick={(event) => {
                                                        deleteResourceDialogFunction.deleteResource(
                                                            projectVersionPublishment.data?.id ?? '',
                                                            projectVersionPublishment.data?.title,
                                                            ResourceType.PROJECT_VERSION_PUBLISHMENT,
                                                            deletePublishment
                                                        );
                                                    }}
                                                    p={2}
                                                >
                                                    <IconBookOff color={'var(--mantine-color-base-button)'} />
                                                </ActionIcon>
                                            </Tooltip>
                                        :   <Tooltip label={'Publish Version'}>
                                                <ActionIcon
                                                    size={'sm'}
                                                    variant={'transparent'}
                                                    onClick={() => {
                                                        OpenModal.StringInputModal(
                                                            'Publish Version',
                                                            (value, multiLineValue) => {
                                                                addPublishment
                                                                    .mutateAsync({
                                                                        resource: {
                                                                            ...defaultProjectVersionPublishmentResource,
                                                                            title: value,
                                                                            changelog: multiLineValue ?? '',
                                                                        },
                                                                        parentId: projectVersionId,
                                                                    })
                                                                    .then();
                                                            },
                                                            undefined,
                                                            'Title',
                                                            true,
                                                            'Changelog',
                                                            undefined,
                                                            true
                                                        );
                                                    }}
                                                    p={2}
                                                >
                                                    <IconBook color={'var(--mantine-color-base-button)'} />
                                                </ActionIcon>
                                            </Tooltip>
                                        }
                                        <ActionIcon
                                            size={'sm'}
                                            onClick={(_) => {
                                                OpenModal.AlertModal(
                                                    'PVT File Download',
                                                    'Do you want to download Project Version as a PVT File?',
                                                    () => {
                                                        dispatch(setImportingOrExportingState(true));
                                                        thunkDispatch(
                                                            startDownloadPVT(
                                                                project.data?.name ?? '',
                                                                projectVersion.data?.id ?? ''
                                                            )
                                                        ).then((value) => {
                                                            if (value) {
                                                                dispatch(setTaskRefresh(true));
                                                            }
                                                            dispatch(setImportingOrExportingState(false));
                                                        });
                                                    }
                                                );
                                            }}
                                            variant={'transparent'}
                                            color={'var(--mantine-color-base-button)'}
                                        >
                                            <IconDownload />
                                        </ActionIcon>
                                    </Group>
                                :   undefined
                            }
                            closeAction={
                                isProcessAdmin ?
                                    (versionId: string) => {
                                        closeProjectVersion.mutate({
                                            id: versionId,
                                        });
                                    }
                                :   undefined
                            }
                            lockAction={
                                isProcessAdmin ?
                                    (versionId) => {
                                        lockProjectVersion.mutate({
                                            id: versionId,
                                        });
                                    }
                                :   undefined
                            }
                            openAction={
                                isProcessAdmin ?
                                    (versionId) => {
                                        openProjectVersion.mutate({
                                            id: versionId,
                                        });
                                    }
                                :   undefined
                            }
                        />
                        <Group>
                            <PropertyInputs.String
                                nullable={true}
                                name={formNames.feedbackLink}
                                control={form.control}
                                propertyValidationInformation={{
                                    resourceType: ResourceType.PROJECT_VERSION,
                                    resourceId: projectVersionId,
                                    propertyIdentifier: ProjectVersionPropertyValidation.FEEDBACK_LINK,
                                }}
                                labelOverride={'Feedback Link'}
                                readOnly={
                                    !isProcessAdmin ||
                                    projectVersion.data?.versionInfo.versionState !== VersionState.OPEN
                                }
                                width={'100%'}
                            />
                            <Show when={projectVersion.data?.versionInfo.versionState === VersionState.OPEN}>
                                <Box pt={26}>
                                    <DisableableIconButtonWithTooltip
                                        disabled={!form.formState.isDirty}
                                        icon={<IconDeviceFloppy />}
                                        onClick={onSubmit}
                                        tooltipText={'Save Feedback Link'}
                                    />
                                </Box>
                            </Show>
                        </Group>
                        <Show when={projectVersionPublishment.data}>
                            <Divider size={'sm'} />
                            <Form_StringInput
                                nullable={false}
                                name={publishmentFormNames.title}
                                control={publishmentForm.control}
                                readOnly={!isAdmin}
                                label={'Baseline'}
                            />
                            <div>
                                <Text fz={14}>Baseline Changelog</Text>
                                <ScrollArea h={300} scrollbars={'y'} offsetScrollbars={'y'} type="auto">
                                    <Paper pl={10} pr={10} withBorder bg={'var(--mantine-color-read-only-input)'}>
                                        <Form_RichText
                                            name={publishmentFormNames.changelog}
                                            form={publishmentForm}
                                            readonly={!isAdmin}
                                        />
                                    </Paper>
                                </ScrollArea>
                            </div>
                        </Show>
                    </Form_EditableViewWrapper>
                </HeightFittedStack>
            </Show>
            <Show when={!isProcessAdmin && projectVersionPublishment.data}>
                <ControlledInputs.String
                    nullable={false}
                    value={projectVersionPublishment.data?.title ?? ''}
                    readOnly={true}
                    label={'Baseline'}
                />
                <div>
                    <Text fz={14}>Baseline Changelog</Text>
                    <ScrollArea h={300} scrollbars={'y'} offsetScrollbars={'y'} type="auto">
                        <Paper pl={10} pr={10} withBorder bg={'var(--mantine-color-read-only-input)'}>
                            <Controlled_RichText
                                value={projectVersionPublishment.data?.changelog ?? ''}
                                readonly={true}
                            />
                        </Paper>
                    </ScrollArea>
                </div>
            </Show>
        </HeightFittedStack>
    );
};
